package ru.arty_bikini.crm_frontend.ui.input.table

import csstype.ClassName
import react.ChildrenBuilder
import react.dom.html.ReactHTML.div
import ru.arty_bikini.crm.dto.enums.HasDisplayValue
import ru.arty_bikini.crm.dto.orders.OrderDTO
import ru.arty_bikini.crm.dto.orders.google.OrderDataTypeDTO
import ru.arty_bikini.crm_frontend.ClientCore
import ru.arty_bikini.crm_frontend.ClientProps
import ru.arty_bikini.crm_frontend.SimpleCache
import ru.arty_bikini.crm_frontend.measure.MeasureType
import ru.arty_bikini.crm_frontend.network.Entity
import ru.arty_bikini.crm_frontend.ui.input.table.data.DataSource
import ru.arty_bikini.crm_frontend.ui.input.types.*
import ru.arty_bikini.crm_frontend.ui.modal.ModalConfig
import kotlin.reflect.KMutableProperty0

external interface TablePanelProps<T : Any> : ClientProps {

    var hintId: String

    var data: DataSource<T>

    var getId: (T) -> String

    var save: suspend (T) -> Unit

    var addNew: (suspend (String) -> Unit)?

    var columns: List<TablePanelColumnProps<T, *>>

    var cache: List<SimpleCache<*>>
}

fun <T : Any> TablePanelProps<T>.savePacked(client: ClientCore, key: String, entity: (T) -> Entity, action: suspend (T) -> Unit) {
    save = action
}

fun <T : Any> TablePanelProps<T>.addForm(title: String, form: ModalConfig<T>, options: TableColumnRenderOptions<T> = TableColumnRenderOptions()) {
    addButton(title, options) { client.event.showModal(form, it) }
}

fun <T : Any> TablePanelProps<T>.addButton(title: String, options: TableColumnRenderOptions<T> = TableColumnRenderOptions<T>(), text: (T) -> String = { title }, onClick: (T) -> Unit) {
    columns = columns + TablePanelColumnProps(
        title = title,
        type = TablePanelColumnType.BUTTON,
        allowNull = false,
        readOnly = false,
        options = options,
        prop = PropertyReference.anonymous({ text(it) }, { entity, value -> onClick(entity) }),
        variants = null,
        converter = StringValueConverter
    )

}

fun <T : Any> TablePanelProps<T>.addDropdown(title: String, options: TableColumnRenderOptions<T> = TableColumnRenderOptions<T>(), content: ChildrenBuilder.(T) -> Unit) {
    columns = columns + TablePanelColumnProps(
        title = title,
        type = TablePanelColumnType.BUTTON_DROPDOWN,
        allowNull = false,
        readOnly = false,
        options = options.copy(extraClasses = { ClassName("dropdown") }, extraContent = {
            div {

                className = ClassName("dropdown-menu")

                content(this, it)
            }
        }),
        prop = PropertyReference.readOnlyAnonymous { title },
        variants = null,
        converter = StringValueConverter
    )

}

val <T : Any> TablePanelProps<T>.addText
    get() = InputTableColumnBuilder(TablePanelColumnType.TEXT_LINE, this, StringValueConverter)

val <T : Any> TablePanelProps<T>.addInt
    get() = InputTableColumnBuilder(TablePanelColumnType.INT, this, IntValueConverter)

val <T : Any> TablePanelProps<T>.addCost
    get() = InputTableColumnBuilder(TablePanelColumnType.INT, this, CostValueConverter)

val <T : Any> TablePanelProps<T>.addBoolean
    get() = InputTableColumnBuilder(TablePanelColumnType.CHECKBOX, this, BooleanValueConverter)

val <T : Any> TablePanelProps<T>.addDate
    get() = InputTableColumnBuilder(TablePanelColumnType.DATE, this, DateValueConverter)

val <T : Any> TablePanelProps<T>.addDateTime
    get() = InputTableColumnBuilder(TablePanelColumnType.DATE_TIME, this, DateValueConverter)

val <T : Any> TablePanelProps<T>.addTextArea
    get() = InputTableColumnBuilder(TablePanelColumnType.TEXT_AREA, this, StringValueConverter)

val <T : Any> TablePanelProps<T>.addSelect
    get() = SelectTableColumnBuilder(this)

val <T : Any> TablePanelProps<T>.addEntity
    get() = SelectTableColumnBuilder(this)

val <T : Any> TablePanelProps<T>.addMultiEntity
    get() = MultiSelectTableColumnBuilder(this)

fun <Extra : Enum<Extra>> TablePanelProps<OrderDTO>.addSpecial(
    flag: FieldFlag.FromGoogleForms,
    title: String,
    type: MeasureType<Extra>,
    special: Extra,
    options: TableColumnRenderOptions<OrderDTO> = TableColumnRenderOptions(),
) {
    val orderDataTypes = client.cache.orderDataTypes

    cache = cache + orderDataTypes

    val column: () -> OrderDataTypeDTO? = {
        orderDataTypes
            .get()
            .find { it.displayCategory == type.category && it.displayPosition == special.name }
    }

    columns = columns + TablePanelColumnProps<OrderDTO, String?>(
        cache = orderDataTypes,
        title = title,
        type = TablePanelColumnType.TEXT_LINE,
        allowNull = true,
        readOnly = true,
        options = options,
        prop = PropertyReference.anonymous(
            getter = { it.dataGoogle?.data?.get(column()?.id) },
            setter = { entity, v ->
                val column = column()
                if (column == null) return@anonymous

                entity.dataGoogle?.let {
                    val data = it.data?.toMutableMap() ?: HashMap()

                    if (v == null) {
                        data.remove(column.id)
                    } else {
                        data[column.id] = v
                    }

                    it.data = data
                }
            },
        ),
        variants = emptyList(),
        converter = StringValueConverter
    )

}
